<template>
  <div>
    <v-alert
      v-if="comp_OrphanedSystems.length > 0"
      type="warning"
      outlined
      dense
      class="mb-3"
    >
      {{
        comp_OrphanedSystems.length === 1
          ? "Ein Wechselsystem ist"
          : comp_OrphanedSystems.length + " Wechselsysteme sind"
      }}
      keiner Grundwaffe zugeordnet:
      <b>{{ comp_OrphanedSystems.map(w => w.KurzT).join(", ") }}</b>.
      Über „Bearbeiten“ kannst du eine Grundwaffe wählen oder das System in
      eine Grundwaffe umwandeln.
    </v-alert>

    <v-data-table
      :headers="comp_Headers"
      :items="weapon"
      sort-by="KurzT"
      :custom-sort="sortGrouped"
      class="elevation-1"
      :item-class="itemRowClass"
    >
      <!-- Wechselsysteme: eingerückt unter ihrer Grundwaffe -->
      <template v-slot:item.funktion_typ="{ item }">
        <div v-if="isSystem(item)" class="d-flex align-center text-no-wrap">
          <v-icon v-if="item.parent_weapon_id" small class="ml-2 mr-1"
            >mdi-subdirectory-arrow-right</v-icon
          >
          <v-chip
            x-small
            :color="item.parent_weapon_id ? 'deep-purple' : 'warning'"
            text-color="white"
          >
            {{ item.parent_weapon_id ? "Wechselsystem" : "ohne Grundwaffe" }}
          </v-chip>
        </div>
        <div v-else class="text-no-wrap">
          <v-chip x-small outlined>Grundwaffe</v-chip>
          <div
            v-if="systemsOf(item).length > 0"
            class="text-caption grey--text"
          >
            + {{ systemsOf(item).length }}
            {{ systemsOf(item).length === 1 ? "Wechselsystem" : "Wechselsysteme" }}
          </div>
        </div>
      </template>

      <!-- NEU: Pool-Status Spalte -->
      <template v-slot:item.pool_status="{ item }">
        <div
          v-if="
            item.is_in_pool && item.pool_clubs && item.pool_clubs.length > 0
          "
        >
          <!-- Badge pro Verein -->
          <v-tooltip bottom v-for="pool in item.pool_clubs" :key="pool.pool_id">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                x-small
                :color="pool.is_active ? 'success' : 'grey'"
                text-color="white"
                class="ma-1"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon x-small left>mdi-target</v-icon>
                {{ pool.club_name }}
              </v-chip>
            </template>
            <span>
              <strong>{{ pool.club_name }}</strong><br />
              Status: {{ pool.is_active ? "Aktiv" : "Inaktiv" }}<br />
              <span v-if="pool.notes">{{ pool.notes }}</span>
            </span>
          </v-tooltip>
        </div>
        <span v-else class="grey--text text-caption">Nicht im Pool</span>
      </template>

      <!-- NEU: Bedürfnis-Spalte -->
      <template v-slot:item.beduerfnis="{ item }">
        <template v-if="user_club === 'UserApp'">
          <v-tooltip bottom v-if="item.beduerfnis">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                x-small
                color="primary"
                outlined
                v-bind="attrs"
                v-on="on"
              >
                {{ getBeduerfnisLabel(item.beduerfnis) }}
              </v-chip>
            </template>
            <span>{{ getBeduerfnisDescription(item.beduerfnis) }}</span>
          </v-tooltip>
          <span v-else class="grey--text text-caption">Nicht angegeben</span>
        </template>
      </template>

      <template v-slot:top>
        <!-- bricht auf schmalen Bildschirmen um statt seitlich rauszulaufen -->
        <div class="d-flex flex-wrap align-center px-4 pt-3 table-actions">
          <!-- Bedürfnis-Check Modal: Nur für User sichtbar -->
          <v-dialog
            v-if="user_club === 'UserApp'"
            v-model="dialogWaffG"
            width="900px"
            scrollable
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                outlined
                color="success success-2"
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click.stop="openWaffGCheck"
              >
                <v-icon left>mdi-gavel</v-icon>
                § 14 Check
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline grey lighten-2">
                Bedürfnis-Check (§ 14 WaffG)
                <v-spacer></v-spacer>
                <v-btn icon @click="close_waffGCompliance">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-0">
                <WaffGComplianceDashboard />
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="close_waffGCompliance"
                >Schließen
                </v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-spacer></v-spacer>
          <v-dialog
            v-model="dialog"
            width="800px"
            scrollable
            :fullscreen="$vuetify.breakpoint.xsOnly"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on">
                <v-icon class="mr-2">mdi-pistol</v-icon>
                Neue Waffe
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span v-if="!editTitle" class="text-h5"
                >Neue Waffe <v-icon class="ml-4">mdi-pistol</v-icon></span
                >
                <!-- Name beim Öffnen gemerkt, damit er sich beim Tippen nicht mitändert -->
                <div v-else class="edit-title">
                  <div class="text-caption grey--text">
                    <v-icon small class="mr-1">mdi-pistol</v-icon>
                    Waffe bearbeiten
                  </div>
                  <div class="text-h5">{{ editTitle.name }}</div>
                  <div v-if="editTitle.details" class="text-body-2 grey--text">
                    {{ editTitle.details }}
                  </div>
                </div>
                <v-spacer></v-spacer>
                <v-btn icon @click="close">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
                <!-- einziger Scrollbereich; Titel und Speichern bleiben stehen -->
                <v-card-text>
                  <v-form ref="form" v-on:submit.prevent>
                    <v-container>
                      <span>(*) - Pflichtfelder</span>
                      <v-row class="mt-3">
                        <v-col cols="12" sm="6">
                          <v-select
                            v-model="editedItem.funktion_typ"
                            :items="FunktionTypen"
                            item-text="label"
                            item-value="value"
                            label="(*) Art"
                            outlined
                            :disabled="comp_FunktionLocked"
                            :hint="comp_FunktionHint"
                            persistent-hint
                          ></v-select>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                          v-if="editedItem.funktion_typ === 2"
                        >
                          <v-select
                            v-model="editedItem.parent_weapon_id"
                            :items="comp_ParentCandidates"
                            item-text="label"
                            item-value="id"
                            label="Grundwaffe"
                            outlined
                            clearable
                            :disabled="editedItem.Transfered === 2"
                            hint="Leer lassen, wenn die Grundwaffe nicht mehr vorhanden ist. Eine geänderte Zuordnung ggf. der Waffenbehörde melden."
                            persistent-hint
                          ></v-select>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="(*) Hersteller"
                            v-model="editedItem.Hersteller"
                            :rules="TextRules"
                            outlined
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            label="(*) Nummer"
                            v-model="editedItem.Nummer"
                            :rules="TextRules"
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="(*) Kaliber"
                            v-model="editedItem.Kaliber"
                            :rules="NWRIDRules"
                            outlined
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            label="(*) Kurztext (Verwendung in allen Ansichten)"
                            v-model="editedItem.KurzT"
                            :rules="TextRules"
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-select
                            v-model="editedItem.Gattung"
                            :items="Gattung"
                            item-text="Waffen Gattung"
                            item-value="value"
                            label="(*) Waffen Gattung"
                            outlined
                            class="mx-1"
                          >
                            <template slot="selection" slot-scope="data">
                              <!-- HTML that describe how select should render selected items -->
                              {{ data.item.value }}
                            </template>
                            <template slot="item" slot-scope="data">
                              <!-- HTML that describe how select should render items when the select is open -->
                              {{ data.item.value }}
                            </template>
                          </v-select>
                        </v-col>
                        <v-col>
                          <v-dialog
                            ref="dialog"
                            v-model="modal"
                            :return-value.sync="editedItem.Erwerb"
                            persistent
                            width="290px"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="editedItem.Erwerbformated"
                                label="(*) Erwerb"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :rules="DatumRules"
                                outlined
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="editedItem.Erwerb"
                              scrollable
                            >
                              <v-spacer></v-spacer>
                              <v-btn
                                text
                                color="primary"
                                @click="modal = false"
                              >
                                Cancel
                              </v-btn>
                              <v-btn
                                text
                                color="primary"
                                @click="$refs.dialog.save(editedItem.Erwerb)"
                              >
                                OK
                              </v-btn>
                            </v-date-picker>
                          </v-dialog>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="WBK Nummer"
                            v-model="editedItem.WBK"
                            outlined
                          ></v-text-field>
                        </v-col>
                        <v-col></v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="NWR-Erlaubnis-ID"
                            v-model="editedItem.NWREID"
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-text-field
                            label="NWR-Waffen-ID"
                            v-model="editedItem.NWRWID"
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <v-row v-if="user_club === 'UserApp'">
                        <v-col cols="12">
                          <v-select
                            v-model="editedItem.inputBeduerfnis"
                            :items="comp_BeduerfnisOptions"
                            item-text="label"
                            item-value="value"
                            label="(*) Bedürfnis"
                            outlined
                            :rules="TextRules"
                            hint="Wofür wurde die Waffe erworben?"
                            persistent-hint
                          >
                            <template v-slot:prepend-inner>
                              <v-icon>mdi-shield-account</v-icon>
                            </template>
                            <template v-slot:item="{ item }">
                              <v-list-item-content>
                                <v-list-item-title>{{
                                    item.label
                                  }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                  {{ item.description }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col>
                          <v-text-field
                            label="Langtext"
                            v-model="editedItem.LangT"
                            outlined
                          ></v-text-field>
                        </v-col>
                        <v-col>
                          <v-text-field
                            label="Beschreibung"
                            v-model="editedItem.Beschreibung"
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row class="mb-5">
                        <v-col>
                          <v-switch
                            v-model="editedItem.export_weapon"
                            label="Export Waffe zu PDF"
                            color="primary"
                            hide-details
                          ></v-switch>
                        </v-col>
                        <v-col>
                          <v-switch
                            v-model="editedItem.ueber_kontigent"
                            label="Über Kontingent"
                            color="primary"
                            hide-details
                          ></v-switch>
                        </v-col>
                        <v-col>
                          <v-switch
                            v-model="editedItem.fremde_waffe"
                            label="Fremde Waffe"
                            color="primary"
                            hide-details
                          ></v-switch>
                        </v-col>
                      </v-row>
                      <v-divider></v-divider>
                      <div v-if="dialogDiszi">
                        <span class="text-h6 my-4"
                        >Disziplin zur Waffe hinzufügen:</span
                        >
                        <v-row class="mt-3 mb-3">
                          <v-col>
                            <!-- Wählen heißt sofort zuordnen -->
                            <DisciplinePicker
                              button
                              :all-items="comp_available_disciplines"
                              @change="save_discipline"
                            />
                          </v-col>
                        </v-row>
                        <v-divider></v-divider>
                        <WeaponDiscipline :waffen_id="editedItem.weaponID">
                        </WeaponDiscipline>
                      </div>
                    </v-container>
                  </v-form>
                </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  Abbrechen
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :loading="loading"
                  @click="save"
                >
                  Speichern
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title class="text-h5 grey lighten-2">
                Willst du diesen Eintrag wirklich löschen?
                <v-spacer></v-spacer>
                <v-btn icon @click="dialogDelete = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text v-if="comp_DeleteBlockingSystems.length > 0" class="pt-4">
                <v-alert type="warning" outlined dense class="mb-0">
                  An dieser Waffe hängen noch Wechselsysteme:
                  <b>{{ comp_DeleteBlockingSystems.map(w => w.KurzT).join(", ") }}</b>.
                  Bitte zuerst über „Bearbeiten“ einer anderen Grundwaffe
                  zuordnen oder die Zuordnung entfernen.
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-btn color="blue darken-1" text @click="closeDelete"
                >Abbrechen
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :disabled="comp_DeleteBlockingSystems.length > 0"
                  @click="deleteItemConfirm()"
                >OK
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <WeaponPDFExport
            :pdf_data="weapon"
            :name="comp_Name"
            :club_user="user_club"
          />

          <v-btn icon @click="get_Weapons">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:item.Erwerb="{ item }">
        <span>{{
            new Date(item.Erwerb.replace(" ", "T")).toLocaleString([], {
              dateStyle: "short"
            })
          }}</span>
      </template>

      <template v-slot:item.export_weapon="{ item }">
        <v-icon v-if="item.export_weapon" color="success">
          mdi-check
        </v-icon>
        <v-icon v-if="!item.export_weapon" color="red">
          mdi-window-close
        </v-icon>
      </template>

      <template v-slot:item.ueber_kontigent="{ item }">
        <v-icon v-if="item.ueber_kontigent" color="success">
          mdi-check
        </v-icon>
        <v-icon v-if="!item.ueber_kontigent" color="red">
          mdi-window-close
        </v-icon>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.Transfered === 0 || item.Transfered === 2"
          class="info my-1 mx-1"
          text
          @click="editItem(item)"
        >
          <v-icon>
            mdi-pencil
          </v-icon>
        </v-btn>

        <v-btn
          v-if="item.Transfered === 0 || item.Transfered === 2"
          class="error my-1"
          text
          @click="deleteItem(item)"
        >
          <v-icon>
            mdi-delete
          </v-icon>
        </v-btn>

        <v-btn
          v-if="item.Transfered === 0 && !isSystem(item)"
          class="my-1 mx-1"
          color="deep-purple"
          text
          title="Wechselsystem zu dieser Waffe anlegen"
          @click="newSystemFor(item)"
        >
          <v-icon>
            mdi-plus-box-multiple
          </v-icon>
        </v-btn>
      </template>
      <template v-slot:item.weapon_pool="{ item }">
        <v-btn
          v-if="user_club === 'UserApp' && item.Transfered === 0"
          class="my-1 mr-1"
          color="success"
          @click="openAddToPool(item)"
          title="In Pool geben"
        >
          <v-icon>mdi-target</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.transfer="{ item }">
        <!-- Zugeordnete Wechselsysteme gehen mit der Grundwaffe — einzeln lehnt das Backend ab -->
        <span
          v-if="isAttachedSystem(item) && item.Transfered !== 1"
          class="text-caption grey--text"
        >
          mit Grundwaffe
        </span>
        <v-btn
          v-else-if="item.Transfered === 0"
          class="my-1"
          color="cyan lighten-1"
          @click="TransferWeaponOpen(item)"
        >
          <v-icon>
            mdi-transfer-right
          </v-icon>
        </v-btn>
        <v-btn
          v-else-if="item.Transfered === 2"
          class="my-1"
          color="blue lighten-1"
          @click="TransferWeaponReturnDialog(item)"
        >
          <v-icon>
            mdi-transfer-left
          </v-icon>
        </v-btn>
      </template>
      <template v-slot:item.Transfered_Until="{ item }">
        <span v-if="item.Transfered === 1 || item.Transfered === 2">{{
            "bis: " +
            new Date(item.Transfered_Until.replace(" ", "T")).toLocaleString(
              [],
              {
                dateStyle: "short"
              }
            )
          }}</span>
        <br />
        <span v-if="item.Transfered === 1">{{
            "von: " + item.Transfered_from_to
          }}</span>
        <span v-if="item.Transfered === 2">{{
            "an: " + item.Transfered_from_to
          }}</span>
      </template>
      <template v-slot:no-data>
        <v-btn color="primary" @click="get_Weapons">
          Reset
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import WeaponDiscipline from "../component/WeaponDiscipline";
import WeaponPDFExport from "@/views/component/WeaponPDFExport.vue";
import DisciplinePicker from "@/views/component/DisciplinePicker.vue";
import WaffGComplianceDashboard from "@/views/component/WaffGComplianceDashboard.vue";
import {
  compareBy,
  groupWithSystems,
  isSystem,
  systemsOf
} from "@/views/component/weaponSystemUtils";

export default {
  name: "weaponTable",
  components: {
    DisciplinePicker,
    WeaponDiscipline,
    WeaponPDFExport,
    WaffGComplianceDashboard
  },
  props: {
    user_club: {
      type: String
    },
    data: {
      type: Object
    },
    weapon: {
      type: Array
    },
    discipline: {
      type: Array
    }
  },
  data: () => ({
    dialog: false,
    dialogDiszi: false,
    editTitle: null,
    dialogDelete: false,
    dialogWaffG: false,
    modal: null,
    loading: false,
    // headers: [
    //   {
    //     align: "KurzT",
    //     sortable: false
    //   },
    //   { text: "WBK", value: "WBK" },
    //   { text: "Beschreibung", value: "Beschreibung" },
    //   { text: "Hersteller", value: "Hersteller" },
    //   { text: "Kaliber", value: "Kaliber" },
    //
    //   { text: "Kurz Text", value: "KurzT" },
    //   { text: "Lang Text", value: "LangT" },
    //   { text: "Nummer", value: "Nummer" },
    //   { text: "Erwerb", value: "Erwerb" },
    //   {
    //     text: "Export PDF",
    //     value: "export_weapon",
    //     sortable: false,
    //     align: "center"
    //   },
    //   {
    //     text: "Über Kontingent",
    //     value: "ueber_kontigent",
    //     sortable: false,
    //     align: "center"
    //   },
    //   {
    //     text: "Bedürfnis",
    //     value: "beduerfnis",
    //     sortable: true,
    //     align: "center"
    //   },
    //   { text: "Actions", value: "actions", sortable: false, align: "center" },
    //   {
    //     text: "Waffenpool",
    //     value: "weapon_pool",
    //     sortable: false,
    //     align: "center"
    //   },
    //   {
    //     text: "Pool-Status",
    //     value: "pool_status",
    //     sortable: false,
    //     align: "center"
    //   },
    //   {
    //     text: "Überlassung",
    //     value: "transfer",
    //     sortable: false,
    //     align: "center"
    //   },
    //   { text: "Überlasssen", value: "Transfered_Until", sortable: false }
    // ],
    editedItem: {
      Beschreibung: null,
      Hersteller: null,
      Kaliber: null,
      KurzT: null,
      LangT: null,
      Nummer: null,
      Erwerb: null,
      Erwerbformated: null,
      export_weapon: false,
      ueber_kontigent: false,
      fremde_waffe: false,
      Gattung: { id: 1, value: "KwGk" },
      WBK: null,
      NWRWID: null,
      NWREID: null,
      inputBeduerfnis: null,
      WaffenDisziplin: null,
      weaponID: null,
      funktion_typ: 1,
      parent_weapon_id: null
    },

    defaultItem: {
      Beschreibung: null,
      Hersteller: null,
      Kaliber: null,
      KurzT: null,
      LangT: null,
      Nummer: null,
      Erwerb: null,
      Erwerbformated: null,
      export_weapon: false,
      ueber_kontigent: false,
      fremde_waffe: false,
      Gattung: { id: 0, value: "" },
      WBK: null,
      NWRWID: null,
      NWREID: null,
      inputBeduerfnis: null,
      WaffenDisziplin: null,
      weaponID: null,
      funktion_typ: 1,
      parent_weapon_id: null
    },

    // Backend: weapons.funktion_typ (1 = Grundwaffe, 2 = Wechselsystem)
    FunktionTypen: [
      { value: 1, label: "Grundwaffe" },
      { value: 2, label: "Wechselsystem / Wechsellauf" }
    ],
    WeaponPrepareDelete: null,

    Gattung: [
      { id: 0, value: "" },
      { id: 1, value: "KwGk" },
      { id: 2, value: "KwKk" },
      { id: 3, value: "LP" },
      { id: 4, value: "LG" },
      { id: 5, value: "LwGk" },
      { id: 6, value: "LwKk" }
    ],
    TextRules: [v => !!v || "Eingabe erforderlich"],
    NWRIDRules: [v => !!v || "Eingabe erforderlich"],
    KaliberRules: [v => !!v || "Eingabe erforderlich"],
    DatumRules: [v => !!v || "Eingabe erforderlich"]
  }),
  computed: {
    comp_AllDiscipline() {
      return this.$store.state.UserApp.Discipline;
    },
    // Katalog ohne die Disziplinen, die schon an der Waffe hängen
    comp_available_disciplines() {
      const assigned = new Set(
        this.$store.getters["UserApp/weapon_Disciplines"](this.editedItem.weaponID).map(d => d.id)
      );
      return (this.discipline || []).filter(d => !assigned.has(d.id));
    },
    comp_BeduerfnisOptions() {
      return this.$store.getters["UserApp/beduerfnisOptions"];
    },
    comp_Name() {
      if (this.user_club == "UserApp") {
        return this.$store.getters["UserAuth/isUserName"];
      } else {
        return this.$store.getters["ClubAuth/isClubName"];
      }
    },
    // Eigene Grundwaffen als Ziel einer Zuordnung (keine überlassenen fremden Waffen)
    comp_ParentCandidates() {
      return (this.weapon || [])
        .filter(
          w =>
            !this.isSystem(w) &&
            w.Transfered !== 1 &&
            w.id !== this.editedItem.weaponID
        )
        .map(w => ({
          id: w.id,
          label: `${w.KurzT} (${w.Kaliber}, Nr. ${w.Nummer})`
        }));
    },
    comp_OrphanedSystems() {
      return (this.weapon || []).filter(
        w => this.isSystem(w) && !w.parent_weapon_id && w.Transfered !== 1
      );
    },
    comp_EditedHasSystems() {
      return (
        !!this.editedItem.weaponID &&
        this.systemsOf({ id: this.editedItem.weaponID }).length > 0
      );
    },
    // Umwandeln ist gesperrt, solange Systeme dranhängen oder die Waffe überlassen ist
    comp_FunktionLocked() {
      return this.comp_EditedHasSystems || this.editedItem.Transfered === 2;
    },
    comp_FunktionHint() {
      if (this.comp_EditedHasSystems)
        return "An dieser Waffe hängen Wechselsysteme – erst diese umhängen, dann umwandeln.";
      if (this.editedItem.Transfered === 2)
        return "Während der Überlassung nicht änderbar.";
      return "Ein Wechselsystem (z. B. Wechsellauf, Oberteil) wird wie eine Waffe mit eigener WBK-Eintragung geführt.";
    },
    comp_DeleteBlockingSystems() {
      return this.WeaponPrepareDelete
        ? this.systemsOf(this.WeaponPrepareDelete)
        : [];
    },
    comp_Headers() {
      return [
        { text: "Art", value: "funktion_typ", sortable: false },
        { text: "WBK", value: "WBK" },
        { text: "Beschreibung", value: "Beschreibung" },
        { text: "Hersteller", value: "Hersteller" },
        { text: "Kaliber", value: "Kaliber" },
        { text: "Kurz Text", value: "KurzT" },
        { text: "Lang Text", value: "LangT" },
        { text: "Nummer", value: "Nummer" },
        { text: "Erwerb", value: "Erwerb" },
        { text: "Export PDF", value: "export_weapon", sortable: false, align: "center" },
        { text: "Über Kontingent", value: "ueber_kontigent", sortable: false, align: "center" },
        ...(this.user_club === "UserApp"
          ? [{ text: "Bedürfnis", value: "beduerfnis", sortable: true, align: "center" }]
          : []),
        { text: "Actions", value: "actions", sortable: false, align: "center" },
        { text: "Waffenpool", value: "weapon_pool", sortable: false, align: "center" },
        { text: "Pool-Status", value: "pool_status", sortable: false, align: "center" },
        { text: "Überlassung", value: "transfer", sortable: false, align: "center" },
        { text: "Überlasssen", value: "Transfered_Until", sortable: false }
      ];
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    "editedItem.Erwerb": function(newVal) {
      if (typeof newVal !== "object" && typeof newVal !== "undefined") {
        this.editedItem.Erwerbformated = new Date(
          newVal.replace(" ", "T")
        ).toLocaleString([], { dateStyle: "short" });
      }
    }
  },
  methods: {
    get_Weapons() {
      this.$store.dispatch(this.user_club + "/getWeapons");
    },
    openAddToPool(item) {
      this.$emit("openAddToPool", item);
    },
    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        var Data = {
          inputWBK: this.editedItem.WBK,
          inputwaffenID: this.editedItem.weaponID,
          inputWGattung: this.editedItem.Gattung,
          inputWaffenNr: this.editedItem.Nummer,
          inputwaffenKaliber: this.editedItem.Kaliber,
          inputwaffenHersteller: this.editedItem.Hersteller,
          inputwaffenTk: this.editedItem.KurzT,
          inputwaffenTL: this.editedItem.LangT,
          inputwaffenBesch: this.editedItem.Beschreibung,
          inputDatErwerb: this.editedItem.Erwerb,
          inputNWRWID: this.editedItem.NWRWID,
          inputNWRWEID: this.editedItem.NWREID,
          inputBeduerfnis: this.editedItem.inputBeduerfnis,
          inputExport: this.editedItem.export_weapon ? 1 : 0,
          input_ueber_kontigent: this.editedItem.ueber_kontigent ? 1 : 0,
          input_fremde_waffe: this.editedItem.fremde_waffe ? 1 : 0,
          inputFunktionTyp: this.editedItem.funktion_typ || 1,
          inputParentWeaponID:
            this.editedItem.funktion_typ === 2
              ? this.editedItem.parent_weapon_id || null
              : null
        };

        try {
          // Wir warten auf die Action. Schlägt die Validierung im Backend fehl,
          // wirft der Store (hoffentlich) einen Fehler oder wir fangen ihn hier ab.
          await this.$store.dispatch(this.user_club + "/setNewWeapon", Data);

          // Nur wenn der Request erfolgreich war, schließen wir das Fenster
          this.dialog = false;
        } catch (error) {
          console.error("Fehler beim Speichern:", error);
          // Das Fenster bleibt offen, der User sieht ggf. Fehlermeldungen (z.B. durch SweetAlert aus der Action)
        } finally {
          this.loading = false; // Loader stoppen, egal ob Erfolg oder Fehler
        }
      }
    },

    editItem(item) {
      this.editedItem = {
        ...item,
        weaponID: item.id,
        NWRWID: item.NWR_WID,
        NWREID: item.NWR_EID,
        Gattung: item.Gattung || "",
        funktion_typ: Number(item.funktion_typ) || 1,
        parent_weapon_id: item.parent_weapon_id
          ? Number(item.parent_weapon_id)
          : null,
        inputBeduerfnis: item.beduerfnis,
        Erwerbformated: item.Erwerb
          ? new Date(item.Erwerb.replace(" ", "T")).toLocaleString([], {
            dateStyle: "short"
          })
          : ""
      };
      this.editTitle = {
        name: item.KurzT || item.Hersteller || "",
        details: [item.Hersteller, item.Kaliber, item.Nummer && "Nr. " + item.Nummer]
          .filter(Boolean)
          .join(" · ")
      };

      this.dialogDiszi = this.user_club === "UserApp";
      if (this.dialogDiszi)
        this.$store.dispatch(this.user_club + "/getAllDiszi");
      this.dialog = true;
    },

    // Neues Wechselsystem, vorbelegt mit Grundwaffe und deren Gattung
    newSystemFor(item) {
      this.editedItem = Object.assign({}, this.defaultItem, {
        funktion_typ: 2,
        parent_weapon_id: item.id,
        Gattung: item.Gattung || "",
        inputBeduerfnis: item.beduerfnis || null
      });
      this.dialogDiszi = false;
      this.dialog = true;
    },

    isSystem(item) {
      return isSystem(item);
    },

    isAttachedSystem(item) {
      return isSystem(item) && !!item.parent_weapon_id;
    },

    systemsOf(item) {
      return systemsOf(this.weapon, item);
    },

    // Sortiert nach der gewählten Spalte, hält Wechselsysteme aber direkt unter ihrer Grundwaffe
    sortGrouped(items, sortBy, sortDesc) {
      return groupWithSystems(
        items,
        compareBy(sortBy && sortBy[0], sortDesc && sortDesc[0])
      );
    },

    save_discipline(item) {
      let Data = {
        waffenID: this.editedItem.weaponID,
        DisziID: item.id
      };
      this.$store.dispatch(this.user_club + "/setDisciplinWeapon", Data);
    },

    addDiscipline() {
      let Data = {
        waffenID: this.editedItem.weaponID,
        DisziID: this.editedItem.WaffenDisziplin.id
      };
      this.$store.dispatch(this.user_club + "/setDisciplinWeapon", Data);
    },

    close() {
      this.dialog = false;
      this.dialogDiszi = false;
      this.editTitle = null;
      if (this.user_club === "UserApp") {
        this.$store.commit(this.user_club + "/get_Diszi_Single_Weapons", []);
      }
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
      if (this.$refs.form) {
        // Validierungsstatus explizit zurücksetzen
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      }
    },

    deleteItemConfirm() {
      let Data = {
        WeaponID: this.WeaponPrepareDelete.id
      };

      this.$store
        .dispatch(this.user_club + "/delWeapon", Data)
        .then((this.dialog = false), (this.dialogDelete = false));
    },

    deleteItem(item) {
      this.WeaponPrepareDelete = item;
      this.dialogDelete = true;
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    TransferWeaponOpen(item) {
      this.$emit("TransferWeaponOpen", item);
    },

    TransferWeaponReturnDialog(item) {
      this.$emit("TransferWeaponReturnDialog", item);
    },

    getBeduerfnisLabel(value) {
      const option = this.comp_BeduerfnisOptions.find(o => o.value === value);
      return option ? option.label : value;
    },

    getBeduerfnisDescription(value) {
      const option = this.comp_BeduerfnisOptions.find(o => o.value === value);
      return option ? option.description : "";
    },

    async openWaffGCheck() {
      // 1. Daten laden (Action im UserApp Modul)
      await this.$store.dispatch("UserApp/get_WaffGCompliance");
      // 2. Dialog öffnen
      this.dialogWaffG = true;
    },

    close_waffGCompliance() {
      this.$store.commit("UserApp/SET_CLEAR_WaffGCompliance");
      this.dialogWaffG = false;
    },

    itemRowClass(item) {
      var Class = null;
      switch (item.Transfered) {
        case 0:
          Class = "";
          break;
        case 1:
          Class = "blue lighten-4";
          break;
        case 2:
          Class = "cyan lighten-5";
          break;
        default:
          Class = "";
      }
      return Class;
    }
  }
};
</script>

<style scoped>
.table-actions {
  column-gap: 8px;
}

.edit-title {
  flex: 1;
  min-width: 0;
  word-break: normal;
  line-height: 1.3;
}

.btn {
  width: 150px;
  text-align: left;
  margin-top: 2px;
  margin-bottom: 2px;
}
</style>